import {
  Component,
  Input,
  OnInit,
  inject,
  signal,
  computed, OnDestroy,
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { FileUploadService } from '../../../documents/services/file-upload.service';
import {
  FileDetails,
  FileUploadStatus,
} from '../../../documents/models/file-details';
import { AzureFileResponse } from '../../../documents/models/azure-file-response';
import { UploadItem } from '../../../../shared/models/upload-item';
import { PaginatedResponse } from '../../../../shared/interfaces/pagination';
import { takeUntil, timer, Subscription } from 'rxjs';
import { FileViewerComponent } from '../../../../shared/components/file-viewer/file-viewer.component';

export interface BusinessDocument {
  id: number;
  file_name: string;
  file_size?: number;
  uploaded_datetime: string;
  upload_status: number; // 1=Pending, 2=Success, 3=Failed, 4=Malicious
}

const MAX_FILES = 3;
const MAX_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

const ACCEPTED_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
];

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

@Component({
  selector: 'app-business-documents',
  templateUrl: './business-documents.component.html',
  styleUrls: ['./business-documents.component.scss'],
  standalone: true,
  imports: [CommonModule, NgTemplateOutlet, FileViewerComponent],
})
export class BusinessDocumentsComponent extends DestroyBase implements OnInit, OnDestroy {
  @Input() profileId?: number;

  private http = inject(HttpClient);
  private apiBaseUrl = environment.apiBaseUrl;
  private fileUploadService = inject(FileUploadService);

  existingFiles = signal<FileDetails[]>([]);
  activeUploadsCount = signal(0);
  private myUploadIds = new Set<string>();
  private pollingSubscription?: Subscription;

  isLoadingFiles = signal(false);
  globalError = signal<string | null>(null);
  /** Track which doc id is pending delete confirmation */
  pendingDeleteId = signal<number | null>(null);

  // ── Consent modal state ──────────────────────────────────────────────────────
  /** Files staged after selection, waiting for consent before upload */
  pendingFiles = signal<File[]>([]);
  /** Whether the consent modal is open */
  showConsentModal = signal(false);
  /** Consent checkbox: rights & permissions */
  hasConsentChecked = signal(false);
  /** Consent checkbox: Terms of Use */
  hasTermsChecked = signal(false);
  /** Whether an upload is in progress (modal Upload button spinner) */
  isUploading = signal(false);

  /** Both checkboxes must be ticked before upload is allowed */
  readonly canProceedWithUpload = computed(
    () => this.hasConsentChecked() && this.hasTermsChecked(),
  );

  // ── File Viewer properties ───────────────────────────────────────────────────
  showFileViewer = false;
  viewerFileUrl = '';
  viewerFileName = '';
  viewerFileId: number | null = null;

  readonly acceptAttr = ACCEPTED_MIME_TYPES.join(',');
  readonly maxFiles = MAX_FILES;
  readonly maxSizeMB = MAX_SIZE_BYTES / (1024 * 1024);

  readonly usedSlots = computed(() => {
    return this.existingFiles().length + this.activeUploadsCount();
  });

  readonly canUploadMore = computed(() => this.usedSlots() < MAX_FILES);
  readonly FileUploadStatus = FileUploadStatus;

  getUploadStatusText(status?: FileUploadStatus | number): string {
    if (!status || status === FileUploadStatus.Success) {
      return '';
    }
    switch (status) {
      case FileUploadStatus.Pending:
        return 'Pending';
      case FileUploadStatus.Failed:
        return 'Failed';
      case FileUploadStatus.Malicious:
        return 'Malicious';
      default:
        return 'Failed';
    }
  }

  getUploadStatusTagClass(status?: FileUploadStatus | number): string {
    switch (status) {
      case FileUploadStatus.Pending:
        return 'status-tag--pending';
      case FileUploadStatus.Failed:
        return 'status-tag--failed';
      case FileUploadStatus.Malicious:
        return 'status-tag--malicious';
      default:
        return 'status-tag--failed';
    }
  }

  ngOnInit(): void {
    this.loadDocuments();
    this.trackUploadProgress();
  }

  override ngOnDestroy(): void {
    this.stopPolling();
    super.ngOnDestroy();
  }

  private trackUploadProgress(): void {
    this.fileUploadService.uploads$
      .pipe(takeUntil(this.destroy$))
      .subscribe((uploads: UploadItem[]) => {
        let hasNewCompletion = false;

        uploads.forEach((u: UploadItem) => {
          if (this.myUploadIds.has(u.id)) {
            if (u.status === 'completed') {
              hasNewCompletion = true;
              this.myUploadIds.delete(u.id);
            } else if (u.status === 'failed' || u.status === 'cancelled') {
              this.myUploadIds.delete(u.id);
            }
          }
        });

        const activeCount = uploads.filter(
          (u: UploadItem) =>
            this.myUploadIds.has(u.id) && u.status === 'uploading',
        ).length;
        this.activeUploadsCount.set(activeCount);

        if (hasNewCompletion) {
          this.closeConsentModal();
          setTimeout(() => this.loadDocuments(), 1000);
        }
      });
  }

  /** Triggered by the hidden file input (from Upload button or drag-drop) */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.openConsentModal(files);
  }

  /** Triggered by the "Add more" input inside the modal */
  onModalFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.addMoreFiles(files);
  }

  /** Remove a single staged file by index; closes modal if list becomes empty */
  removePendingFile(index: number): void {
    const updated = this.pendingFiles().filter((_, i) => i !== index);
    if (updated.length === 0) {
      this.closeConsentModal();
    } else {
      this.pendingFiles.set(updated);
      this.dismissError();
    }
  }

  /** Merge additional files into the pending list (validates each one) */
  addMoreFiles(files: File[]): void {
    this.dismissError();
    const currentCount = this.pendingFiles().length;
    const availableSlots = MAX_FILES - this.usedSlots() - currentCount;

    if (availableSlots <= 0) {
      this.globalError.set(
        `You can upload a maximum of ${MAX_FILES} documents.`,
      );
      setTimeout(() => this.globalError.set(null), 6000);
      return;
    }

    const filesToProcess = files.slice(0, availableSlots);
    const validFiles: File[] = [];
    const existingNames = new Set(this.pendingFiles().map((f) => f.name));

    for (const file of filesToProcess) {
      if (existingNames.has(file.name)) continue; // skip duplicates already staged

      const errors: string[] = [];
      if (file.size > MAX_SIZE_BYTES) {
        errors.push(
          `"${file.name}" exceeds the ${this.maxSizeMB} MB size limit.`,
        );
      }
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        errors.push(`"${file.name}" has an unsupported type.`);
      }
      if (file.type && !ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase())) {
        errors.push(`"${file.name}" has an invalid MIME type.`);
      }
      const baseName = file.name.includes('.')
        ? file.name.substring(0, file.name.lastIndexOf('.'))
        : file.name;
      if (!/^[a-zA-Z0-9 .\-_]+$/.test(baseName)) {
        errors.push(`"${file.name}" has an invalid file name.`);
      }

      if (errors.length > 0) {
        this.globalError.set(errors[0]);
        setTimeout(() => this.globalError.set(null), 6000);
      } else {
        validFiles.push(file);
        existingNames.add(file.name);
      }
    }

    if (validFiles.length > 0) {
      this.pendingFiles.update((prev) => [...prev, ...validFiles]);
    }
  }

  /** Validate files and open the consent modal */
  openConsentModal(files: File[]): void {
    this.globalError.set(null);

    const availableSlots = MAX_FILES - this.usedSlots();
    if (availableSlots <= 0) {
      this.globalError.set(
        `You can upload a maximum of ${MAX_FILES} documents.`,
      );
      setTimeout(() => this.globalError.set(null), 6000);
      return;
    }

    const filesToProcess = files.slice(0, availableSlots);
    if (files.length > availableSlots) {
      this.globalError.set(
        `Only ${availableSlots} more file(s) can be uploaded. Extra files were skipped.`,
      );
      setTimeout(() => this.globalError.set(null), 6000);
    }

    const validFiles: File[] = [];

    for (const file of filesToProcess) {
      const errors: string[] = [];

      if (file.size > MAX_SIZE_BYTES) {
        errors.push(
          `"${file.name}" exceeds the ${this.maxSizeMB} MB size limit.`,
        );
      }

      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        errors.push(
          `"${file.name}" has an unsupported type. Accepted: PDF, Word, Excel, Images.`,
        );
      }

      if (file.type && !ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase())) {
        errors.push(`"${file.name}" has an invalid MIME type.`);
      }

      const baseName = file.name.includes('.')
        ? file.name.substring(0, file.name.lastIndexOf('.'))
        : file.name;
      const safeFileNamePattern = /^[a-zA-Z0-9 .\-_]+$/;
      if (!safeFileNamePattern.test(baseName)) {
        errors.push(
          `"${file.name}" has an invalid name. Only letters, numbers, spaces, hyphens, underscores, and dots are allowed.`,
        );
      }

      if (errors.length > 0) {
        this.globalError.set(errors[0]);
        setTimeout(() => this.globalError.set(null), 6000);
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    // Stage files and open the consent modal
    this.pendingFiles.set(validFiles);
    this.hasConsentChecked.set(false);
    this.hasTermsChecked.set(false);
    this.showConsentModal.set(true);
  }

  /** Called by the modal Cancel button */
  closeConsentModal(): void {
    this.showConsentModal.set(false);
    this.pendingFiles.set([]);
    this.hasConsentChecked.set(false);
    this.hasTermsChecked.set(false);
    this.isUploading.set(false);
  }

  /** Called by the modal Upload button — both checkboxes must be ticked */
  submitUpload(): void {
    if (!this.canProceedWithUpload() || this.isUploading()) return;

    const validFiles = this.pendingFiles();
    if (validFiles.length === 0) return;

    this.isUploading.set(true);
    const fileNames = validFiles.map((f) => f.name);

    this.fileUploadService.uploadSMEFiles(fileNames).subscribe({
      next: (responses: AzureFileResponse[]) => {
        responses.forEach((res: AzureFileResponse, index: number) => {
          const file = validFiles[index];
          if (file && res) {
            const uploadId = this.fileUploadService.startSMEUpload(res, file);
            this.myUploadIds.add(uploadId);
          }
        });
        const activeCount = this.fileUploadService.uploads$.value.filter(
          (u: UploadItem) =>
            this.myUploadIds.has(u.id) && u.status === 'uploading',
        ).length;
        this.activeUploadsCount.set(activeCount);
        // Close modal immediately so user doesn't have to wait
        this.closeConsentModal();
      },
      error: (err: HttpErrorResponse) => {
        console.error('SME upload initiation failed:', err);
        const errMsg = this.getErrorMessage(
          err,
          'Upload initiation failed. Please try again.',
        );
        this.globalError.set(errMsg);
        this.isUploading.set(false);
        setTimeout(() => this.globalError.set(null), 8000);
      },
    });
  }

  private checkAndManagePolling(files: FileDetails[]): void {
    const hasPending = files.some(
      (f) => f.upload_status === FileUploadStatus.Pending,
    );

    if (hasPending) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  private startPolling(): void {
    if (this.pollingSubscription && !this.pollingSubscription.closed) {
      return;
    }

    this.pollingSubscription = timer(10000, 10000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadDocuments(true);
      });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  loadDocuments(isPoll = false): void {
    if (!isPoll) {
      this.isLoadingFiles.set(true);
    }
    this.fileUploadService.getAllUploadedFiles(1, 10, true).subscribe({
      next: (res: PaginatedResponse<FileDetails>) => {
        const files = res.results ?? [];
        this.existingFiles.set(files);
        if (!isPoll) {
          this.isLoadingFiles.set(false);
        }
        this.checkAndManagePolling(files);
      },
      error: () => {
        if (!isPoll) {
          this.existingFiles.set([]);
          this.isLoadingFiles.set(false);
        }
        this.stopPolling();
      },
    });
  }

  confirmDelete(docId: number): void {
    this.pendingDeleteId.set(docId);
  }

  cancelDelete(): void {
    this.pendingDeleteId.set(null);
  }

  deleteDocument(docId: number): void {
    this.fileUploadService.deleteFile(docId).subscribe({
      next: () => {
        this.existingFiles.update((prev) => prev.filter((d) => d.id !== docId));
        this.pendingDeleteId.set(null);
        this.checkAndManagePolling(this.existingFiles());
      },
      error: (err: HttpErrorResponse) => {
        console.error('Delete failed:', err);
        const errMsg = this.getErrorMessage(
          err,
          'Failed to delete the document. Please try again.',
        );
        this.globalError.set(errMsg);
        this.pendingDeleteId.set(null);
        setTimeout(() => this.globalError.set(null), 8000);
      },
    });
  }

  getFileIconType(
    fileName: string,
  ): 'pdf' | 'word' | 'excel' | 'image' | 'generic' {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['xls', 'xlsx'].includes(ext)) return 'excel';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
    return 'generic';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatFileSizeFromFile(file: File): string {
    return this.formatFileSize(file.size);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private getErrorMessage(err: HttpErrorResponse, fallback: string): string {
    if (err?.error?.detail) {
      if (Array.isArray(err.error.detail)) {
        return err.error.detail.join(' ');
      } else if (typeof err.error.detail === 'string') {
        return err.error.detail;
      }
    }
    if (err?.error?.non_field_errors?.[0]) {
      return err.error.non_field_errors[0];
    }
    if (err?.error?.message) {
      return err.error.message;
    }
    if (err?.error && typeof err.error === 'object') {
      const keys = Object.keys(err.error).filter((k) => k !== 'errorId');
      for (const key of keys) {
        const val = err.error[key];
        if (
          Array.isArray(val) &&
          val.length > 0 &&
          typeof val[0] === 'string'
        ) {
          return val[0];
        } else if (typeof val === 'string') {
          return val;
        }
      }
    }
    return fallback;
  }

  viewFile(file: FileDetails, event?: Event): void {
    if (event) {
      const target = event.target as HTMLElement;
      if (
        target.closest('.biz-doc-actions-row') ||
        target.closest('.biz-doc-confirm-row') ||
        target.closest('button')
      ) {
        return;
      }
    }

    if (this.pendingDeleteId() === file.id) {
      return;
    }

    if (file.upload_status && file.upload_status !== FileUploadStatus.Success) {
      if (file.upload_status === FileUploadStatus.Pending) {
        this.globalError.set('File upload is still processing. Please wait.');
      } else if (file.upload_status === FileUploadStatus.Malicious) {
        this.globalError.set(
          'This file was flagged as malicious and cannot be opened.',
        );
      } else {
        this.globalError.set('File upload failed or document is unavailable.');
      }
      setTimeout(() => this.globalError.set(null), 6000);
      return;
    }

    this.fileUploadService
      .downloadFile(file.id, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.viewerFileId = file.id;
          this.viewerFileUrl = response.download_url;
          this.viewerFileName =
            response.file_name || file.file_name || file.blob_name;
          this.showFileViewer = true;
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to view file:', err);
          const errMsg = this.getErrorMessage(
            err,
            'Failed to open the document preview. Please try again.',
          );
          this.globalError.set(errMsg);
          setTimeout(() => this.globalError.set(null), 8000);
        },
      });
  }

  onCloseViewer(): void {
    this.showFileViewer = false;
    this.viewerFileUrl = '';
    this.viewerFileName = '';
    this.viewerFileId = null;
  }

  onViewerDownload(): void {
    if (this.viewerFileId) {
      this.fileUploadService
        .downloadFile(this.viewerFileId, false)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const link = document.createElement('a');
            link.href = response.download_url;
            link.download = response.file_name;
            link.click();
          },
          error: (err: HttpErrorResponse) => {
            console.error('Download failed:', err);
            const errMsg = this.getErrorMessage(
              err,
              'Failed to download the document. Please try again.',
            );
            this.globalError.set(errMsg);
            setTimeout(() => this.globalError.set(null), 8000);
          },
        });
    }
  }

  dismissError(): void {
    this.globalError.set(null);
  }
}
